import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import flyTextLogo from '@fly/assets/images/fly_text_logo.svg';
import { Button, Text } from '@fly/core';
import { FlySvgSprite } from '@fly/svg';

import styles from './FlyErrorPage.module.less';

interface FlyErrorPageProps {
  title?: ReactNode;
  actions?: ReactNode;
  content?: ReactNode;
  orientation?: 'vertical' | 'horizontal';
}

export const FlyErrorPage = ({
  title,
  actions,
  content,
  orientation = 'vertical',
}: FlyErrorPageProps) => {
  const { t } = useTranslation();

  if (orientation === 'horizontal') {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <div className='mx-4 flex max-w-[656px] items-center gap-fly-6'>
          <div className='flex flex-col gap-fly-5'>
            <img src={flyTextLogo} alt='JFrog Fly' className='w-[132px]' />
            <Text size='6' weight='bold' highContrast>
              {title}
            </Text>
            <Text size='6' highContrast>
              {content}
            </Text>
            <div>{actions}</div>
          </div>
          <FlySvgSprite
            name='404'
            size={220}
            className='sm:block hidden flex-shrink-0 flex-grow-1 opacity-48'
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <FlySvgSprite name='404' size={168} className='mb-fly-6 opacity-48' />
      <div className={styles.wrapper}>
        <div className='flex flex-col items-center justify-center'>
          <Text size='9' weight='medium' highContrast className='text-center'>
            {title ||
              t('app.oops_page.title', {
                defaultValue: 'Oops!',
              })}
          </Text>
          <Text size='7' highContrast className='mt-fly-5 text-center'>
            {content ||
              t('app.oops_page.subtitle', {
                defaultValue: 'Something went wrong. Please refresh your page.',
              })}
          </Text>
          <div className='mt-fly-9'>
            {actions || (
              <Button
                className={styles.refreash}
                variant='surface'
                color='neutral'
                size='3'
                onClick={() => window.location.reload()}
              >
                {t('app.oops_page.cta', {
                  defaultValue: 'Refresh',
                })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
